import { useIntl } from "react-intl";

import { FeaturedInfoCard } from "@/components/adg4/featured-info-card/src/featured-info-card";
import { IconSvgProps } from "@/libraries/wpl/foundations/src/icons/types";

import { useLocalizedImage } from "../../use-localized-image";

import { FeaturedInfoSection, PLAN } from "./featured-info";

const ArrowUpRightIcon = ({ size }: IconSvgProps) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 25"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M23.3995 3.61928L2.89918 24.1196L0 21.2204L20.5003 0.720098L23.3995 3.61928Z"
            fill="#1868DB"
        />
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M19.8999 4.21969L10.3533 4.21969V0.119629L24 0.119629V13.7664H19.8999V4.21969Z"
            fill="#1868DB"
        />
    </svg>
);

const CheckmarkIcon = ({ size }: IconSvgProps) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 25"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M0 0.119629H17.6V17.7196H0V0.119629ZM14.7313 6.05093L7.19992 13.5823L2.86855 9.25093L5.1313 6.98818L7.19992 9.05681L12.4686 3.78818L14.7313 6.05093Z"
            fill="#1868DB"
        />
        <path
            d="M20.8 7.31963V20.1196H8V23.3196H24V7.31963H20.8Z"
            fill="#1868DB"
        />
    </svg>
);

const ListIcon = ({ size }: IconSvgProps) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 25"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M24 6.5482L0 6.5482V3.11963L24 3.11963V6.5482Z"
            fill="#1868DB"
        />
        <path
            d="M16.2857 21.9768H7.71429V18.5482H16.2857V21.9768Z"
            fill="#1868DB"
        />
        <path
            d="M4.28571 14.2625L19.7143 14.2625V10.8339L4.28571 10.8339L4.28571 14.2625Z"
            fill="#1868DB"
        />
    </svg>
);

const WorkflowIcon = ({ size }: IconSvgProps) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 25"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M16.4284 0.119629H7.85693V6.97677H10.4286V10.4053H3.57143V17.2625H1V24.1196H9.57143V17.2625H7V13.8339H17.2857V17.2625H14.7143V24.1196H23.2857V17.2625H20.7143V10.4053H13.8571V6.97677H16.4284V0.119629Z"
            fill="#1868DB"
        />
    </svg>
);

export const PlanSection = () => {
    const intl = useIntl();

    return (
        <FeaturedInfoSection
            variant={PLAN}
            eyebrow={intl.formatMessage({
                id: "JiraSoftwareFeaturesPageV2.TabsSection.Plan.Label",
                defaultMessage: "Plan"
            })}
            header={intl.formatMessage({
                id: "JiraSoftwareFeaturesPageV2.HighlightPlanSection.Header",
                defaultMessage: "Start your next project off right"
            })}
            description={intl.formatMessage({
                id: "JiraSoftwareFeaturesPageV2.HighlightPlanSection.Description",
                defaultMessage:
                    "Structure and align goals across teams, break work down into manageable tasks, and know who’s working on what."
            })}
        >
            <FeaturedInfoCard
                image={{
                    image: {
                        src: useLocalizedImage("planListViewImage"),
                        alt: intl.formatMessage({
                            id: "JiraSoftwareFeaturesPageV2.HighlightPlanSection.Card.ListView.ImageAlt",
                            defaultMessage: "List view in Jira"
                        })
                    }
                }}
                Icon={ArrowUpRightIcon}
                title={intl.formatMessage({
                    id: "JiraSoftwareFeaturesPageV2.HighlightPlanSection.Card.ListView.Header",
                    defaultMessage: "List view"
                })}
                body={intl.formatMessage({
                    id: "JiraSoftwareFeaturesPageV2.HighlightPlanSection.Card.ListView.Description",
                    defaultMessage:
                        "Swap out the spreadsheets. Organize work into a single list view and update, sort, and prioritize on the fly with in-line editing."
                })}
            />
            <FeaturedInfoCard
                image={{
                    image: {
                        src: useLocalizedImage("planCalendarImage"),
                        alt: intl.formatMessage({
                            id: "JiraSoftwareFeaturesPageV2.HighlightPlanSection.Card.Calendar.ImageAlt",
                            defaultMessage: "Calendar view in Jira"
                        })
                    }
                }}
                Icon={CheckmarkIcon}
                title={intl.formatMessage({
                    id: "JiraSoftwareFeaturesPageV2.HighlightPlanSection.Card.Calendar.Header",
                    defaultMessage: "Calendar"
                })}
                body={intl.formatMessage({
                    id: "JiraSoftwareFeaturesPageV2.HighlightPlanSection.Card.Calendar.Description",
                    defaultMessage:
                        "Use Jira’s calendar view to easily visualize work over time and teams to hit every deadline, together."
                })}
            />
            <FeaturedInfoCard
                image={{
                    image: {
                        src: useLocalizedImage("planSprintsBacklogImage"),
                        alt: intl.formatMessage({
                            id: "JiraSoftwareFeaturesPageV2.HighlightPlanSection.Card.SprintsBacklog.ImageAlt",
                            defaultMessage: "Backlog view in Jira"
                        })
                    }
                }}
                Icon={ListIcon}
                title={intl.formatMessage({
                    id: "JiraSoftwareFeaturesPageV2.HighlightPlanSection.Card.SprintsBacklog.Header",
                    defaultMessage: "Sprints & backlog"
                })}
                body={intl.formatMessage({
                    id: "JiraSoftwareFeaturesPageV2.HighlightPlanSection.Card.SprintsBacklog.Description",
                    defaultMessage:
                        "Agile teams can utilize Jira’s backlog to prioritize all their work, and tackle a subset of the work in their next sprint."
                })}
            />
            <FeaturedInfoCard
                image={{
                    image: {
                        src: useLocalizedImage("planTimelineImage"),
                        alt: intl.formatMessage({
                            id: "JiraSoftwareFeaturesPageV2.HighlightPlanSection.Card.Timeline.ImageAlt",
                            defaultMessage: "Timeline view in Jira"
                        })
                    }
                }}
                Icon={WorkflowIcon}
                title={intl.formatMessage({
                    id: "JiraSoftwareFeaturesPageV2.HighlightPlanSection.Card.Timeline.Header",
                    defaultMessage: "Timeline"
                })}
                body={intl.formatMessage({
                    id: "JiraSoftwareFeaturesPageV2.HighlightPlanSection.Card.Timeline.Description",
                    defaultMessage:
                        "See how all work rolls up to the big picture. Understand timing, duration, and dependencies in a single project or across multiple."
                })}
            />
        </FeaturedInfoSection>
    );
};
